import { useId } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import { ChevronDown } from '../icon'
import styles from './ExpandableRow.module.css'

export interface ExpandableRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onToggle'> {
  /**
   * The row itself — laid out however the caller likes. The toggle sits behind
   * it, so links in the summary (a player's name to their profile) stay links.
   */
  summary: ReactNode
  /** What opens under the row. */
  children?: ReactNode
  /** Controlled: the caller owns which rows are open, so it can offer "Expand all". */
  expanded: boolean
  onToggle?: (expanded: boolean) => void
  /**
   * The row can't open — nothing to show, or not allowed to show it. It renders
   * as a plain row: no button, no chevron, nothing to tab to.
   */
  locked?: boolean
  /** Draw the border in `--expandable-row-highlight` (default the strong border) — a captain, say. */
  highlight?: boolean
  /** An accessible name for the toggle when the summary alone doesn't make one. */
  toggleLabel?: string
}

/**
 * A list row that opens in place to show more — a player in a roster opening
 * onto their scouting report. The toggle button covers the whole row from
 * behind, so a tap anywhere opens it, while links and buttons in the summary
 * sit above it and keep working (a link can't live inside a button). The
 * button is named by the summary unless `toggleLabel` says otherwise; the
 * panel is a region it controls.
 */
export function ExpandableRow({
  summary,
  children,
  expanded,
  onToggle,
  locked = false,
  highlight = false,
  toggleLabel,
  className,
  ...props
}: ExpandableRowProps) {
  const panelId = useId()
  const summaryId = useId()
  const open = expanded && !locked
  const classes = [styles.row, highlight && styles.highlight, open && styles.open, className].filter(Boolean).join(' ')

  return (
    <div className={classes} {...props}>
      {locked ? (
        <div className={styles.summary}>
          <div className={styles.content}>{summary}</div>
          {/* Holds the chevron's width, so a locked row's columns line up with
              the openable rows above and below it. */}
          <span className={styles.chevronSpace} aria-hidden="true" />
        </div>
      ) : (
        <div className={`${styles.summary} ${styles.toggleable}`}>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={toggleLabel}
            aria-labelledby={toggleLabel ? undefined : summaryId}
            onClick={() => onToggle?.(!open)}
          />
          <div id={summaryId} className={`${styles.content} ${styles.above}`}>{summary}</div>
          <ChevronDown size={16} className={`${styles.chevron} ${styles.above}`} aria-hidden="true" />
        </div>
      )}
      {!locked && (
        <div id={panelId} role="region" aria-label={toggleLabel} hidden={!open} className={styles.panel}>
          {open && children}
        </div>
      )}
    </div>
  )
}
